/**
 * Post-login "where should your letters arrive?" screen (spec:
 * 2026-07-06-email-coverage-design.md §2). Shown once, right after the auth
 * handshake succeeds, when the signed-in user has zero member rows with an
 * email on file and hasn't skipped this screen before. Both buttons proceed
 * to the app; only `not now` is remembered (via localStorage) so the screen
 * never nags on later logins — /settings remains the path back.
 */

import { apiFetch } from "./api.js";
import { t } from "./i18n.js";

const ASKED_KEY = "snail_email_asked";

/** Pure gating logic, unit-tested separately from the DOM/network side. */
export function shouldShowEmailAsk(
  hasAnyEmail: boolean,
  alreadyAsked: boolean
): boolean {
  return !hasAnyEmail && !alreadyAsked;
}

/** Whether a `POST /me/email` response warrants the soft "saved, but
 *  couldn't confirm delivery" notice (spec §7, decided 2026-07-10).
 *  Only `welcome_sent === false` (confirmation attempted and failed) is a
 *  soft-notice case — `true` (sent) and `null` (never attempted, e.g. no
 *  Resend key configured) both proceed silently, since nothing went wrong
 *  from the user's perspective in either of those. */
export function shouldShowWelcomeSoftNotice(welcomeSent: boolean | null): boolean {
  return welcomeSent === false;
}

function markAsked(): void {
  localStorage.setItem(ASKED_KEY, "1");
}

/** Fetches email-state, and if warranted, renders the ask screen into `root`.
 *  Always calls `onDone` exactly once — immediately if the screen is skipped,
 *  or after the user picks save/not-now. Never throws: an API failure here
 *  must not block login. */
export async function maybeRenderEmailAsk(
  root: HTMLElement,
  onDone: () => void
): Promise<void> {
  const alreadyAsked = localStorage.getItem(ASKED_KEY) === "1";
  let hasAnyEmail = true;
  try {
    const state = await apiFetch("/me/email-state");
    hasAnyEmail = Boolean(state?.has_any_email);
  } catch {
    onDone();
    return;
  }

  if (!shouldShowEmailAsk(hasAnyEmail, alreadyAsked)) {
    onDone();
    return;
  }

  renderEmailAsk(root, onDone);
}

/** Renders the ask screen unconditionally (used by the join-page success view,
 *  which has its own ordering — spec §2 "Join-page ordering"). */
export function renderEmailAsk(root: HTMLElement, onDone: () => void): void {
  root.innerHTML = `
    <div class="email-ask">
      <h3>${t("email_ask.title")}</h3>
      <p>${t("email_ask.body")}</p>
      <div class="form-group">
        <input id="email-ask-input" type="email" placeholder="${t("email_ask.placeholder")}">
      </div>
      <button class="btn" id="email-ask-save">${t("email_ask.save")}</button>
      <button class="btn-secondary" id="email-ask-skip">${t("email_ask.skip")}</button>
      <div id="email-ask-error" class="error-banner" style="display:none"></div>
      <div id="email-ask-notice" class="notice-banner" style="display:none"></div>
    </div>`;

  const showError = (m: string) => {
    const e = root.querySelector<HTMLElement>("#email-ask-error")!;
    e.style.display = "block";
    e.textContent = m;
  };

  const showNotice = (m: string) => {
    const n = root.querySelector<HTMLElement>("#email-ask-notice")!;
    n.style.display = "block";
    n.textContent = m;
  };

  root.querySelector("#email-ask-skip")!.addEventListener("click", () => {
    markAsked();
    onDone();
  });

  root.querySelector("#email-ask-save")!.addEventListener("click", async () => {
    const input = root.querySelector<HTMLInputElement>("#email-ask-input")!;
    const email = input.value.trim();
    if (!email) {
      markAsked();
      onDone();
      return;
    }
    const btn = root.querySelector<HTMLButtonElement>("#email-ask-save")!;
    btn.disabled = true;
    try {
      const result = await apiFetch("/me/email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email_address: email }),
      });
      if (shouldShowWelcomeSoftNotice(result?.welcome_sent ?? null)) {
        showNotice(t("email_ask.welcome_failed"));
      }
      markAsked();
      onDone();
    } catch (e: any) {
      btn.disabled = false;
      showError(e.message || t("email_ask.err_save"));
    }
  });
}
